import { useEffect, useState } from 'react';
import type { AdminUser, AdminDefaults, AdminRoute, ModalState } from './types';
import { Icon, Avatar } from './atoms';
import { seedUsers, CAPS } from './data';
import { currentUserClaims } from '../auth/cognito';
import { Overview } from './views/Overview';
import { Users } from './views/Users';
import { Approvals } from './views/Approvals';
import { AIUsage } from './views/AIUsage';
import { Storage } from './views/Storage';
import { Misuse } from './views/Misuse';
import { Settings } from './views/Settings';
import { UserDrawer } from './overlays/UserDrawer';
import { FlowModal } from './overlays/FlowModal';
import './admin.css';

const NAV: { id: AdminRoute; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview', icon: 'grid' },
  { id: 'users', label: 'Users', icon: 'users' },
  { id: 'approvals', label: 'Approvals', icon: 'inbox' },
  { id: 'ai', label: 'AI usage', icon: 'spark' },
  { id: 'storage', label: 'Storage', icon: 'hdd' },
  { id: 'misuse', label: 'Misuse', icon: 'alert' },
  { id: 'settings', label: 'Settings', icon: 'gear' },
];

const TITLES: Record<AdminRoute, [string, string]> = {
  overview: ['System overview', 'Users, AI cost and storage at a glance'],
  users: ['Users', 'Manage accounts, access and AI caps'],
  approvals: ['Approvals', 'Review new registrations'],
  ai: ['AI usage', 'Tokens & cost, system-wide and per user'],
  storage: ['Storage', 'Database and S3 footprint per user'],
  misuse: ['Misuse & abuse', 'Signals worth a closer look'],
  settings: ['Settings', 'Default caps, registration and alerting'],
};

function adminInitials(): string {
  const name = currentUserClaims().name;
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.map(p => p[0]).slice(0, 2).join('').toUpperCase();
}

export function AdminScreen() {
  const [theme, setTheme] = useState<'light' | 'dark'>(() =>
    (localStorage.getItem('jotly.admin.theme') as 'light' | 'dark') || 'light'
  );
  const [route, setRoute] = useState<AdminRoute>('overview');
  const [users, setUsers] = useState<AdminUser[]>(() => seedUsers.map(u => ({ ...u })));
  const [query, setQuery] = useState('');
  const [drawer, setDrawer] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [defaults, setDefaults] = useState<AdminDefaults>({
    free: { ...CAPS.free }, approved: { ...CAPS.approved },
    requireApproval: true, freeBeforeApproval: true,
    warnPct: 90, spikeX: 2, ratePerMin: 100, failMax: 5,
  });

  useEffect(() => { localStorage.setItem('jotly.admin.theme', theme); }, [theme]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const drawerUser = drawer ? users.find(u => u.id === drawer) ?? null : null;

  function patchUser(id: string, patch: Partial<AdminUser>) {
    setUsers(list => list.map(u => u.id === id ? { ...u, ...patch } : u));
  }

  function act(type: string, arg?: unknown) {
    if (type === 'goto') { setRoute(arg as AdminRoute); return; }
    if (type === 'decline') {
      const u = arg as AdminUser;
      patchUser(u.id, { status: 'disabled' });
      setToast(`${u.name}'s registration declined`);
      return;
    }
    setModal({ type: type as ModalState['type'], user: arg as AdminUser });
  }

  function onModalDone(msg: string, patch?: Partial<AdminUser>) {
    if (patch && modal?.user) patchUser(modal.user.id, patch);
    setModal(null);
    if (msg) setToast(msg);
  }

  const pendingCount = users.filter(u => u.status === 'pending').length;
  const flaggedCount = users.filter(u => u.flags.length > 0).length;

  const viewProps = { users, onOpen: (u: AdminUser) => setDrawer(u.id), act, query };
  const [t1, t2] = TITLES[route];

  let view: React.ReactNode;
  switch (route) {
    case 'overview': view = <Overview {...viewProps} />; break;
    case 'users': view = <Users {...viewProps} />; break;
    case 'approvals': view = <Approvals {...viewProps} />; break;
    case 'ai': view = <AIUsage users={users} onOpen={(u: AdminUser) => setDrawer(u.id)} />; break;
    case 'storage': view = <Storage users={users} onOpen={(u: AdminUser) => setDrawer(u.id)} />; break;
    case 'misuse': view = <Misuse {...viewProps} />; break;
    case 'settings': view = <Settings defaults={defaults} setDefaults={setDefaults} />; break;
  }

  return (
    <div className={'admin ' + (theme === 'dark' ? 'dir-slate-dark' : 'dir-slate')}>
      {/* Sidebar */}
      <div className="a-side">
        <div className="a-brand">
          <span className="wm">Jotly<span className="dot" /></span>
          <span className="tag">Admin</span>
        </div>
        <div className="a-nav">
          <div className="a-navlbl">Operations</div>
          {NAV.slice(0, 3).map(n => navLink(n))}
          <div className="a-navlbl">Cost & safety</div>
          {NAV.slice(3, 6).map(n => navLink(n))}
          <div className="a-navlbl">Configure</div>
          {NAV.slice(6).map(n => navLink(n))}
        </div>
        <div className="a-side-foot">
          <div className="a-me">
            <Avatar size={34} hue="#1b212b" initials={adminInitials()} />
            <div className="who">
              <div className="n">{currentUserClaims().name ?? 'Admin'}</div>
              <div className="r">Owner</div>
            </div>
            <button className="themebtn" title="Toggle theme"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={17} />
            </button>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="a-main">
        <div className="a-top">
          <div>
            <h1>{t1}</h1>
            <div className="sub">{t2}</div>
          </div>
          <div className="grow" />
          {route === 'users' && (
            <div className="a-search">
              <Icon name="search" size={16} />
              <input placeholder="Search name or email…" value={query}
                onChange={e => setQuery(e.target.value)} />
            </div>
          )}
          <button className="btn" onClick={() => setRoute('settings')}>
            <Icon name="gear" size={15} />Settings
          </button>
        </div>
        <div className="a-scroll">{view}</div>
      </div>

      {/* Overlays */}
      {drawerUser && (
        <UserDrawer user={drawerUser} onClose={() => setDrawer(null)}
          onAction={(type, u) => act(type, u)} />
      )}
      {modal && (
        <FlowModal type={modal.type} user={modal.user}
          onClose={() => setModal(null)} onDone={onModalDone} />
      )}
      {toast && <div className="toast"><Icon name="check" size={16} />{toast}</div>}
    </div>
  );

  function navLink(n: typeof NAV[number]) {
    const badge = n.id === 'approvals' && pendingCount
      ? <span className="ct accent">{pendingCount}</span>
      : n.id === 'misuse' && flaggedCount
        ? <span className="ct warn">{flaggedCount}</span>
        : null;
    return (
      <a key={n.id} className={route === n.id ? 'on' : ''}
        onClick={e => { e.preventDefault(); setRoute(n.id); setQuery(''); }}>
        <Icon name={n.icon} size={18} />{n.label}{badge}
      </a>
    );
  }
}
